/**
 * 018: COPIED from
 *   vendor/isomorphic/apps/isomorphic/src/app/auth/(sign-up)/sign-up-1/page.tsx (wrapper/title
 *   shape, for its field set — this is an invitation accept, not self-service sign-up)
 *   wrapper: vendor/isomorphic/apps/isomorphic/src/app/shared/auth-layout/auth-wrapper-one.tsx
 *   with the field markup of vendor/isomorphic/apps/isomorphic/src/app/auth/(sign-in)/sign-in-1/
 *   sign-in-form.tsx
 *   summary: vendor/isomorphic/packages/isomorphic-core/src/ui/avatar-card.tsx
 *   flourish: vendor/isomorphic/packages/isomorphic-core/src/components/shape/underline.tsx
 *
 * The wrapper is the template's own file, imported from @steelyard/theme, banner column and hero
 * image included — the same composition sign-in.tsx and Otp.tsx in this folder use. The fields are
 * rizzui `Input`/`Password` with the template's own size, label and input classes, wrapped in the
 * template's own `Form` (@core/ui/form.tsx). The invitation is summarised by the template's own
 * `AvatarCard` (who and as what) in one bordered box, with the expiry under it — not a card of
 * account-settings rows, whose `py-10` blocks pushed the name field below the fold for three facts.
 *
 * Only the data, labels, routes and business rules are ours.
 */
import { useState } from "react";
import { Link, useLoaderData, useNavigate, type LoaderFunctionArgs } from "react-router-dom";
import type { SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { Button, Input, Password, Text } from "rizzui";
import { AuthWrapperOne, AvatarCard, UnderlineShape, Form, FieldError } from "@steelyard/theme";
import { acceptInvitation, endSession, invitationNeedsPassword, previewInvitation } from "../../lib/identity";
import { useTurnstile } from "../../components/turnstile";
import { formatMoment, roleName } from "../../lib/format";
import heroImage from "../../assets/auth/sign-in-hero.svg";

function InvitationHero() {
  return (
    <div className="relative mx-auto aspect-[4/3.37] h-[calc(100vh-31rem)] max-w-full">
      <img src={heroImage} alt="" className="absolute inset-0 size-full rounded-lg object-contain" />
    </div>
  );
}

const BANNER_TITLE = "Infra Junction's operations, in one back office.";
const BANNER_DESCRIPTION =
  "Orders, credit, GST compliance and warehouse dispatch — the same system your colleagues use, kept current with what the API actually returns.";

function Underlined({ children }: { children: string }) {
  return (
    <span className="relative inline-block">
      {children}
      <UnderlineShape className="absolute -bottom-2 start-0 h-2.5 w-full text-blue xl:-bottom-1.5" />
    </span>
  );
}

/**
 * Ported from apps/admin/app/(auth)/invitations/[token]/page.tsx + components/invitation-form.tsx.
 *
 * The preview happens before anything is submitted (FR-013) — wired as a route `loader` (the SPA
 * equivalent of the Server Component read the Next version did before rendering) rather than a
 * `useEffect`, so the token param is resolved and the preview fetched before this component ever
 * paints, avoiding a loading-then-form flash.
 */
type Invitation = { email: string | null; role: string | null; expiresAt: string | null };

/** Identity's preview is `{ email, role, expiresAt, passwordRequired }`; anything missing is left out. */
function invitationOf(body: unknown): Invitation {
  const raw = (body ?? {}) as Record<string, unknown>;
  const text = (value: unknown) => (typeof value === "string" && value.length > 0 ? value : null);
  return { email: text(raw.email), role: text(raw.role), expiresAt: text(raw.expiresAt) };
}

export async function invitationLoader({ params }: LoaderFunctionArgs) {
  const token = params.token ?? "";
  const { data, error } = await previewInvitation(token);
  return { token, data, error };
}

const acceptSchema = z.object({
  displayName: z.string().trim().min(1, "Name is required").max(200),
  password: z.string().min(1, "Password is required"),
});
type AcceptValues = z.infer<typeof acceptSchema>;
/** A passwordless role (warehouse staff, company users) signs in by one-time code: name only. */
const nameOnlySchema = acceptSchema.extend({ password: z.string() });

export default function InvitationAcceptPage() {
  const { token, data, error } = useLoaderData() as Awaited<ReturnType<typeof invitationLoader>>;
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [acceptedAs, setAcceptedAs] = useState<string | null>(null);
  const captcha = useTurnstile("accept_invitation");

  if (error) {
    return (
      <AuthWrapperOne
        title="This invitation cannot be used"
        description="Invitations expire, and each one can be accepted once. Ask whoever invited you to send another."
        bannerTitle={BANNER_TITLE}
        bannerDescription={BANNER_DESCRIPTION}
        isSocialLoginActive={false}
        pageImage={<InvitationHero />}
      >
        <FieldError error={error ?? { title: "The invitation was refused", status: 400 }} />
        <Text className="mt-6 text-center leading-loose text-gray-500 lg:mt-8 lg:text-start">
          <Link to="/sign-in" className="font-semibold text-gray-700 transition-colors hover:text-blue">Go to sign in</Link>
        </Text>
      </AuthWrapperOne>
    );
  }

  const invitation = invitationOf(data);
  const needsPassword = invitationNeedsPassword(data);

  // A passwordless staff role (spec §5: the Dispatcher, "One-time code") works from the admin
  // mobile app, so accepting ends here — on how to sign in there — instead of in the dashboard. The
  // preview's `passwordRequired: false` decides it, not the role name (rule 3).
  // ponytail: a back-office-only role added to Identity's PasswordlessRoles lands here too; the
  // "sign in with a code" link below still takes it to the back office.
  if (acceptedAs !== null) {
    return (
      <AuthWrapperOne
        title={<>You are all <Underlined>set.</Underlined></>}
        description={`Your account is ready, ${acceptedAs}. Sign in on the Infra Junction admin app on your phone.`}
        bannerTitle={BANNER_TITLE}
        bannerDescription={BANNER_DESCRIPTION}
        isSocialLoginActive={false}
        pageImage={<InvitationHero />}
      >
        <ol className="list-decimal space-y-3 ps-5 text-sm text-gray-700">
          <li>Open the Infra Junction admin app on your phone.</li>
          <li>
            Enter {invitation.email ? <span className="font-semibold text-gray-900">{invitation.email}</span> : "the email this invitation was sent to"}.
          </li>
          <li>Type the 6-digit code we email you. There is no password.</li>
        </ol>
        <Text className="mt-8 text-center leading-loose text-gray-500 lg:text-start">
          At a desk instead?{" "}
          <Link to="/otp" className="font-semibold text-gray-700 transition-colors hover:text-blue">Sign in to the back office with a code</Link>
        </Text>
      </AuthWrapperOne>
    );
  }

  const onSubmit: SubmitHandler<AcceptValues> = async (values) => {
    setSubmitError(null);
    try {
      await acceptInvitation(token, values.displayName, needsPassword ? values.password : undefined, await captcha.getToken());
    } catch (err) {
      setSubmitError(err);
      return;
    } finally {
      captcha.reset(); // single-use: a retry needs a fresh token
    }
    if (needsPassword) {
      navigate("/", { replace: true });
      return;
    }
    // The BFF wrote a session for this browser; nobody is going to use it, so it does not stay.
    void endSession();
    setAcceptedAs(values.displayName.trim());
  };

  return (
    <AuthWrapperOne
      title={<>Welcome <Underlined>aboard.</Underlined></>}
      description={
        needsPassword
          ? "Set your name and a password to finish your account."
          : "Set your name to finish your account. There is no password: you sign in with a 6-digit code we email you."
      }
      bannerTitle={BANNER_TITLE}
      bannerDescription={BANNER_DESCRIPTION}
      isSocialLoginActive={false}
      pageImage={<InvitationHero />}
    >
      <div className="mb-6 rounded-lg border border-muted p-4">
        <AvatarCard
          src=""
          name={invitation.email ?? "Your invitation"}
          description={invitation.role ? `Invited as ${roleName(invitation.role)}` : "This invitation is valid."}
        />
        {invitation.expiresAt ? (
          <Text className="mt-3 text-xs text-gray-500">This link works until {formatMoment(invitation.expiresAt)}.</Text>
        ) : null}
      </div>

      {submitError ? (
        <div className="mb-5">
          <FieldError error={submitError} />
        </div>
      ) : null}

      <Form<AcceptValues>
        validationSchema={needsPassword ? acceptSchema : nameOnlySchema}
        onSubmit={onSubmit}
        useFormProps={{ defaultValues: { displayName: "", password: "" } }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <div className="space-y-5">
            <Input size="lg" className="[&>label>span]:font-medium" inputClassName="text-sm" label="Your name" placeholder="e.g. Ravi Kumar" autoFocus maxLength={200} autoComplete="name" helperText="Colleagues see this on anything you approve or change." error={errors.displayName?.message} {...register("displayName")} />
            {needsPassword ? (
              <Password label="Choose a password" size="lg" className="[&>label>span]:font-medium" inputClassName="text-sm" autoComplete="new-password" helperText="The service decides what it will accept and says so if it refuses." error={errors.password?.message} {...register("password")} />
            ) : null}
            {captcha.widget}

            <Button type="submit" className="w-full" size="lg" isLoading={isSubmitting}>
              {needsPassword ? "Accept and continue" : "Accept invitation"}
            </Button>
          </div>
        )}
      </Form>

      <Text className="mt-6 text-center leading-loose text-gray-500 lg:mt-8 lg:text-start">
        Already have an account?{" "}
        <Link to="/sign-in" className="font-semibold text-gray-700 transition-colors hover:text-blue">Sign in</Link>
      </Text>
    </AuthWrapperOne>
  );
}
